import { useState, type FormEvent } from 'react';
import { siteInfo, timeSlots } from '@/mocks/feed';

const FORM_ID = 'night-subscribe-form';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function SubscribeForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    // 蜜罐检查：命中则不提交，仅给出通用反馈
    const honey = String(formData.get('website_alt') || '').trim();
    if (honey) {
      setStatus('success');
      setFormError('');
      formEl.reset();
      return;
    }

    formData.delete('website_alt');
    const email = String(formData.get('email') || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setFormError('请填写一个有效的邮箱地址，方便我们把夜里的整理发给你。');
      return;
    }

    setStatus('submitting');
    setFormError('');

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/dah2rd3p14a1h3mr6bb0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK';

      if (isOk && !isSpam) {
        setStatus('success');
        setFormError('');
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(
          isSpam
            ? '提交未通过校验，请稍后再试一次。'
            : serverMsg || '订阅暂时没有成功，请稍后重试。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络好像不太稳定，稍后再试一次吧。');
    }
  };

  return (
    <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-5 md:p-8">
      <div className="mb-5 flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-500/15 text-accent-300">
          <i className="ri-mail-send-line text-xl" />
        </span>
        <div>
          <h3 className="font-heading text-lg font-bold text-foreground-950">订阅深夜整理</h3>
          <p className="mt-1 text-sm leading-relaxed text-foreground-600">
            每天早上八点，把夜里发酵过的话题整理成一封短邮件发给你。不想看的时候随时可以退订。
          </p>
        </div>
      </div>

      <form
        id={FORM_ID}
        data-readdy-form
        onSubmit={handleSubmit}
        noValidate
        className="flex flex-col gap-3 sm:flex-row sm:items-stretch"
      >
        <label className="sr-only" htmlFor="sub-email">
          邮箱地址
        </label>
        <input
          id="sub-email"
          type="email"
          name="email"
          required
          autoComplete="email"
          placeholder="你的邮箱地址"
          className="h-11 flex-1 rounded-md border border-background-200 bg-background-50 px-4 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/25"
        />
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="h-11 whitespace-nowrap rounded-md bg-primary-500 px-5 font-label text-sm text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'submitting' ? '提交中…' : '立即订阅'}
        </button>
      </form>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="font-label text-xs text-foreground-600">你最常在线的时段</span>
          <select
            name="slot"
            className="h-10 cursor-pointer rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          >
            {timeSlots.map((s) => (
              <option key={s.key} value={s.key}>
                {s.key}（{s.range}）
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-label text-xs text-foreground-600">想让我们关注的线索方向（选填）</span>
          <input
            type="text"
            name="topic_hint"
            maxLength={80}
            placeholder="例如：夜间直播舆情"
            className="h-10 rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
          />
        </label>
      </div>

      <label className="mt-3 flex flex-col gap-1.5">
        <span className="font-label text-xs text-foreground-600">补充说明（选填，最多 500 字）</span>
        <textarea
          name="message"
          rows={3}
          maxLength={500}
          placeholder="想补充的线索背景、希望我们核查的时间点等"
          className="resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
        />
      </label>

      {/* 反垃圾字段：普通文本输入，通过样式表隐藏 */}
      <input
        type="text"
        name="website_alt"
        className="alt-contact-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === 'success' ? (
        <p className="mt-4 flex items-center gap-2 rounded-md border border-accent-500/30 bg-accent-500/10 px-3 py-2.5 text-sm text-accent-200">
          <i className="ri-checkbox-circle-line" />
          订阅成功，明早的整理会准时到你的邮箱。也可以随时写信到 {siteInfo.email}。
        </p>
      ) : null}

      {status === 'error' && formError ? (
        <p className="mt-4 flex items-center gap-2 rounded-md border border-primary-500/40 bg-primary-500/10 px-3 py-2.5 text-sm text-primary-200">
          <i className="ri-error-warning-line" />
          {formError}
        </p>
      ) : null}

      <p className="mt-4 text-xs leading-relaxed text-foreground-500">
        我们只把它用来发送整理邮件，不会用于其他用途。若表单无法提交，也可以直接写信到 {siteInfo.email}。
      </p>
    </div>
  );
}